import { Modal } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Col, Row, Select, Spin } from "antd";
import styled from "styled-components";
import { groupBy, orderBy, capitalize } from "lodash";
import moment from "moment-timezone";
import { ResponsiveContainer, PieChart, Pie, Cell, Legend, Sector, Tooltip } from "recharts";
import { useExams } from "store/exams/selectors";
import { useCurrentOrg, useExamGraphData } from "store/organizations/selectors";
import { handleListExamClassGraphData, handleListExamGraphData } from "store/organizations/thunks";
import { COLOR } from "utils/constants/color";
import { useCurrentUser } from "store/users/selectors";
import { useCurrentClass } from "store/classes/selectors";

const { Option } = Select;

const Button = styled.button`
    border: none;
    cursor: pointer;
    outline: none;
    transition: all 300ms ease;
    height: 40px;
    background: ${COLOR.green200};
    color: #fff;
    padding-left: 12px;
    padding-right: 12px;
    &:hover {
      background: ${COLOR.green1000};
    }
`;
const Title = styled.div`
  font-size: 16px;
  color: #333;
  font-weight: bold;
  margin-bottom: 10px;
  
  @media screen and (max-width: 480px) {
    font-size: 14px;
    margin-bottom: 8px;
  }
`;

const ChartContainer = styled.div`
  padding: 24px;
  background: #fff;
  border-radius: 8px;
  position: relative;
  
  @media screen and (max-width: 768px) {
    padding: 16px;
  }
  
  @media screen and (max-width: 480px) {
    padding: 12px;
  }
`;

const ChartContainerLoader = styled.div`
    position: absolute;
    inset: 0;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: -11%;
`;

const GraphRow = styled(Row)`
  display: flex;
  flex-direction: row;
  
  @media screen and (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const ExamButton = styled.button`
     background-color: ${COLOR.green200};
     width: 130px;
     height:30px;
     border-radius:5px;
     margin-left:5px;
     margin-right:5px;
     border: 1px solid ${p => p.active ? "#f6ebeb" : "transparent"};
     cursor: pointer;
     box-sizing: border-box;
     box-shadow:${p => p.active ? "2px 2px 2px rgb(35 53 89 / 19%), -2px -2px 2px rgb(35 53 89 / 19%)" : "none"};
     color:rgb(255,255,255);

     @media screen and (max-width: 496px){
        width: 45%;
        flex: 1 auto;
        margin-left:0px;
        margin-top:3px;
        margin-bottom:3px;
     }
    @media screen and (max-width: 372px){
        width: 100%;
    }
}
`;

const GraphCol = styled(Col)`
  display: flex;
  justify-content: flex-end;
  
  @media screen and (max-width: 768px) {
    justify-content: flex-start;
    flex-direction: column;
    gap: 12px;
  }
  
  @media screen and (max-width: 480px) {
    gap: 8px;
  }
`;

const ORDERED_DATA = [
  "NOVICE LOW", "NOVICE MID", "NOVICE HIGH",
  "INTER LOW", "INTER MID", "INTER HIGH",
  "ADV LOW", "ADV MID", "ADV HIGH"
];

const COLORS = [
  "#FF6B81", "#4A90E2", "#1A3263", "#48D1CC",
  "#FF8C00", "#FF69B4", "#008B8B", "#8E44AD", "#C06C84"
];

const initialGraphData = ORDERED_DATA.reduce((acc, level) => {
  acc[level] = 0;
  return acc;
}, {});

const examTypeToSelectValue = (examType) => {
  if (examType === "exam1") return "1";
  if (examType === "exam2") return "2";
  if (examType === "exam3") return "3";
  if (examType === "combined") return "combine";
  return "1";
};

const examSchoolToSelectValue = (examSchool) => {
  if (examSchool === "HIGH SCHOOL" || examSchool === "high") return "high";
  if (examSchool === "MIDDLE SCHOOL" || examSchool === "middle") return "middle";
  return "";
};

const renderActiveShape = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  startAngle,
  endAngle,
  fill,
  payload,
  percent,
  value,
  isMobile,
}) => {
  const RADIAN = Math.PI / 180;
  const sin = Math.sin(-RADIAN * (midAngle ?? 1));
  const cos = Math.cos(-RADIAN * (midAngle ?? 1));
  const sx = (cx ?? 0) + ((outerRadius ?? 0) + 10) * cos;
  const sy = (cy ?? 0) + ((outerRadius ?? 0) + 10) * sin;
  const mx = (cx ?? 0) + ((outerRadius ?? 0) + 30) * cos;
  const my = (cy ?? 0) + ((outerRadius ?? 0) + 30) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * 22;
  const ey = my;
  const textAnchor = cos >= 0 ? 'start' : 'end';

  return (
    <g>
      {/* <text x={cx} y={cy} dy={8} textAnchor="middle" fill={fill}>
        {isMobile ? payload.name.substring(0, 6) + "..." : payload.name}
      </text> */}
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={(outerRadius ?? 0) + 6}
        outerRadius={(outerRadius ?? 0) + 10}
        fill={fill}
      />
      {/* <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" /> */}
      {/* <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" /> */}
      {/* <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} textAnchor={textAnchor} fill="#333">{`PV ${value}`}</text> */}
      {/* <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} dy={18} textAnchor={textAnchor} fill="#999">
        {`(Rate ${((percent ?? 1) * 100).toFixed(2)}%)`}
      </text> */}
    </g>
  );
};

const CustomTooltip = ({ active, payload, label, chartData }) => {
  if (!active || !payload || !payload.length || !chartData || !chartData.length) return null;
  const total = chartData.reduce((sum, item) => sum + (item.value ?? 0), 0);

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #ddd",
        borderRadius: 8,
        padding: "10px 12px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
      }}
    >
      {label ? <p style={{ margin: 0, fontWeight: 600 }}>{label}</p> : null}
      {payload.map((entry, index) => {
        const value = entry.value ?? 0;
        const percent = total > 0 ? ((value / total) * 100).toFixed(1) : "0.0";
        const fillColor = entry?.fill || COLORS[index % COLORS.length];
        return (
          <div key={index} style={{ marginTop: 6, color: '#000' }}>
            {entry.name}: {percent}%
          </div>
        );
      })}
    </div>
  );
};

export const OverallProficiencyChart = ({ selectedClassId = "", currentOrg, currentUser, examSchool='high', examType, selectedActiveSession }) => {
  const currentClass = useCurrentClass();
  const [selectedExam, setSelectedExam] = useState(() => examTypeToSelectValue(examType));
  const [selectedSchool, setSelectedSchool] = useState(() => examSchoolToSelectValue(examSchool));
  const [activeSession, setActiveSession] = useState(() => selectedActiveSession || "");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 480);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 768);

  const dispatch = useDispatch();
  const [graphData, setGraphData] = useState(initialGraphData);
  const [loading, setLoading] = useState(false);
  const examGraphData = useExamGraphData();
  const exams = useExams();

  // Get available sessions from exams data, sorted by latest year first
  const availableSessions = useMemo(() => {
    const sessions = Object.keys(groupBy(exams, "session"));
    return sessions.sort((a, b) => {
      // Extract years from session strings like "2024-2025"
      const yearA = parseInt(a.split('-')[0]);
      const yearB = parseInt(b.split('-')[0]);
      return yearB - yearA; // Sort descending (latest first)
    });
  }, [exams]);

  useEffect(() => {
    setSelectedExam(examTypeToSelectValue(examType));
    setSelectedSchool(examSchoolToSelectValue(examSchool));
    if (selectedActiveSession) {
      setActiveSession(selectedActiveSession);
    }
  }, [examType, examSchool, selectedActiveSession]);

  // Set initial session when exams data is available
  useEffect(() => {
    if (availableSessions.length > 0 && !activeSession) {
      // Try to find the current academic year session first
      const currentYear = moment().year();
      const currentMonth = moment().get("month");
      const expectedSession = currentMonth < 7 
        ? `${currentYear - 1}-${currentYear}`
        : `${currentYear}-${currentYear + 1}`;
      
      // If expected session exists, use it; otherwise use the first available
      const initialSession = availableSessions.includes(expectedSession) 
        ? expectedSession 
        : availableSessions[0];
      
      setActiveSession(initialSession);
    }
  }, [availableSessions, activeSession]);

  // Handle window resize for responsive chart
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 480);
      setIsTablet(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch data when filters change
  useEffect(() => {
    if (!activeSession) return; // Don't fetch if no session is selected
    
    (async () => {
      setLoading(true);
      try {
        if (currentUser?.role === 'teacher') {
          const data = await dispatch(
            handleListExamClassGraphData({
              session: activeSession,
              exam_type: selectedExam,
              school_level: selectedSchool,
              id:  selectedClassId ?  selectedClassId : currentClass.id
            })
          );
          setGraphData(typeof data === "object" ? data : initialGraphData);
        } else {
          const data = await dispatch(
            handleListExamGraphData({
              session: activeSession,
              exam_type: selectedExam,
              school_level: selectedSchool,
              id: currentOrg.id
            })
          );
          setGraphData(typeof data === "object" ? data : initialGraphData);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [dispatch, selectedExam, selectedSchool, activeSession, currentOrg, currentClass, currentUser]);

  // Prepare chart data
  const chartData = useMemo(() => {
    const keys = orderBy(
      Object.keys(graphData),
      element => ORDERED_DATA.indexOf(element)
    );
    const formatted = keys.map(key => ({
      name: capitalize(key),
      value: graphData[key]
    })).filter(item => item.value > 0);

    const total = formatted.reduce((sum, item) => sum + item.value, 0);
    if (total === 0) {
      return [{ name: "No Data", value: 1, fill: "#404040" }];
    }
    return formatted;
  }, [graphData, selectedExam, selectedSchool, activeSession]);

  return (
    <ChartContainer>
      
      <GraphRow>
        <Col xs={24} sm={24} md={12}>
          <Title>Overall Proficiency</Title>
        </Col>
        <GraphCol xs={24} sm={24} md={12}>
          <Select
            value={selectedSchool}
            style={{ 
              width: isTablet ? '100%' : 140, 
              marginRight: isTablet ? 0 : 10,
              marginBottom: isTablet ? 8 : 0
            }}
            onChange={setSelectedSchool}
          >
            <Option value="high">High School</Option>
            <Option value="middle">Middle School</Option>
          </Select>
          <Select
            value={selectedExam}
            style={{ 
              width: isTablet ? '100%' : 120, 
              marginRight: isTablet ? 0 : 10,
              marginBottom: isTablet ? 8 : 0
            }}
            onChange={setSelectedExam}
          >
            <Option value="1">Exam1</Option>
            <Option value="2">Exam2</Option>
            <Option value="3">Exam3</Option>
            <Option value="combine">Combine</Option>
          </Select>
          <Select
            value={activeSession}
            style={{ 
              width: isTablet ? '100%' : 140
            }}
            onChange={setActiveSession}
          >
            {availableSessions.map(session => (
              <Option value={session} key={session}>
                {session}
              </Option>
            ))}
          </Select>
        </GraphCol>
      </GraphRow>

      <ResponsiveContainer width="100%" style={{ marginTop: '20px', position: 'relative' }} height={isMobile ? 250 : 300}>

        {loading && (
          <ChartContainerLoader>
            <Spin size="large" />
          </ChartContainerLoader>
        )}

        <PieChart>
          <Pie
            activeShape={(props) => renderActiveShape({ ...props, isMobile })}
            data={chartData}
            cx="50%"
            cy="40%"
            innerRadius={isMobile ? 30 : 60}
            outerRadius={isMobile ? 45 : 80}
            dataKey="value"
            // label={!isMobile}
            labelLine={false}
            gap={10}
            hoverAnimation={false}
          >
            {chartData &&chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.fill || COLORS[index % COLORS.length]}
              />
            ))}
          </Pie>
          <Tooltip content={(props) => <CustomTooltip {...props} chartData={chartData} />} /> 
          <Legend 
            formatter={(value, entry) => {
              const total = chartData.reduce((sum, item) => sum + item.value, 0);
              const percentage = total > 0 ? ((entry.payload.value / total) * 100).toFixed(1) : 0;
              return `${value} (${percentage}%)`;
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};

const OverallModal = ({ selectedClassId = "", examSchool='high', examType, selectedActiveSession }) => {
  const [open, setOpen] = useState(false);
  const currentOrg = useCurrentOrg();
  const currentUser = useCurrentUser();

  return (
    <>
    {currentUser?.role === 'teacher' ? (
      <ExamButton className="apExamSelect" type="primary" onClick={() => setOpen(true)}>View Overall Graph</ExamButton>
    ) : (
      <Button style={{marginTop: '18px', borderRadius:'8px'}} className="apExamSelect" type="primary" onClick={() => setOpen(true)}>
        View Overall Graph
      </Button>
    )}
      <Modal
        title=""
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        destroyOnClose
        width="90%"
        style={{ maxWidth: 800 }}
        centered
      >
        {open ? (
          <OverallProficiencyChart selectedClassId={selectedClassId} currentOrg={currentOrg} currentUser={currentUser} examSchool={examSchool} examType={examType} selectedActiveSession={selectedActiveSession}
          />
        ) : null}
      </Modal>
    </>
  );
};

export default OverallModal;